import { apiClient, getAuthHeaders } from './api';

export interface OrderItem {
  id: string;
  product_id: string;
  product_name: string;
  epicor_part_number: string;
  quantity: number;
  unit_price: number;
  total_price: number;
}

export type OrderSource = 'portal' | 'epicor';

export interface Order {
  id: string;
  order_number: string;
  customer_id: string;
  status: 'DRAFT' | 'SUBMITTED' | 'CONFIRMED' | 'SHIPPED' | 'DELIVERED' | 'CANCELLED' | 'INVOICED' | 'CLOSED';
  // total_amount is the post-discount, pre-tax subtotal (legacy field name).
  // Prefer the explicit breakdown fields below when rendering UI.
  total_amount: number;
  original_total_amount?: number | null;
  total_discount_amount?: number | null;
  subtotal_amount?: number | null;
  tax_rate?: number | null;
  tax_amount?: number | null;
  grand_total_amount?: number | null;
  currency: string;
  order_date: string;
  delivery_date?: string;
  shipping_date?: string;
  invoice_date?: string;
  epicor_order_status?: string;
  epicor_order_num?: number;
  last_status_sync_at?: string;
  // Origin of the order. 'portal' = placed via the customer portal,
  // 'epicor' = imported from Epicor (sales-rep / offline order).
  // Drives the Online vs Offline tab in the orders page and read-only
  // treatment (no Cancel / Reorder / Edit) for offline orders.
  created_via?: OrderSource;
  // Primary sales rep on the order (Epicor SalesRepCode1 / Name1).
  // Populated only for offline orders. Phone + contact-center are
  // intentionally not on the model yet — pending Mowilex Ops data source.
  sales_rep_code?: string | null;
  sales_rep_name?: string | null;
  notes?: string;
  items: OrderItem[];
  item_count: number;
  created_at: string;
  updated_at: string;
}

export interface OrderCountsBySource {
  portal: number;
  epicor: number;
  total: number;
}

export interface CreateOrderFromCartRequest {
  delivery_date?: string; // YYYY-MM-DD format
  notes?: string;
  ship_to_address_id?: string;
}

export interface UpdateOrderRequest {
  shipping_address?: string;
  billing_address?: string;
  notes?: string;
  status?: Order['status'];
}

export interface UpdateOrderStatusRequest {
  status: Order['status'];
}

export interface OrderListParams {
  limit?: number;
  offset?: number;
  status_filter?: Order['status'];
  /** Restrict to portal-placed orders ('portal') or sales-rep offline
   *  orders ('epicor'). Omit to return both. */
  source?: OrderSource;
}

export type ReorderMode = 'merge' | 'replace';

export interface ReorderSkippedItem {
  product_id: string;
  product_name?: string | null;
  epicor_part_number?: string | null;
  quantity: number;
  reason: string;
}

export interface ReorderResponse {
  success: boolean;
  mode: ReorderMode;
  added_count: number;
  merged_count: number;
  cleared_count: number;
  skipped: ReorderSkippedItem[];
  message: string;
}

class OrderService {
  /**
   * Get list of orders for current user
   */
  async getOrders(params: OrderListParams = {}): Promise<Order[]> {
    try {
      const headers = getAuthHeaders();
      const queryParams = new URLSearchParams();

      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.offset) queryParams.append('offset', params.offset.toString());
      if (params.status_filter) queryParams.append('status_filter', params.status_filter);
      if (params.source) queryParams.append('source', params.source);

      const endpoint = `/orders/?${queryParams.toString()}`;
      const response = await apiClient.get<Order[]>(endpoint, headers);

      return response;
    } catch (error) {
      console.error('Get orders error:', error);
      throw error;
    }
  }

  /** Count of the customer's orders grouped by source. Used to render
   *  the badge next to each tab ("Online (12) / Offline (3)"). */
  async getCountsBySource(): Promise<OrderCountsBySource> {
    return apiClient.get<OrderCountsBySource>(
      '/orders/counts-by-source',
      getAuthHeaders(),
    );
  }

  /**
   * Create new order from cart
   */
  async createOrderFromCart(orderData: CreateOrderFromCartRequest): Promise<Order> {
    try {
      const headers = getAuthHeaders();
      const response = await apiClient.post<Order>('/orders/create-from-cart', orderData, headers);
      return response;
    } catch (error) {
      console.error('Create order from cart error:', error);
      throw error;
    }
  }

  /**
   * Get order by ID
   */
  async getOrderById(orderId: string): Promise<Order> {
    try {
      const headers = getAuthHeaders();
      const response = await apiClient.get<Order>(`/orders/${orderId}`, headers);
      return response;
    } catch (error) {
      console.error('Get order by ID error:', error);
      throw error;
    }
  }

  /**
   * Update order details
   */
  async updateOrder(orderId: string, updateData: UpdateOrderRequest): Promise<Order> {
    try {
      const headers = getAuthHeaders();
      const response = await apiClient.put<Order>(`/orders/${orderId}`, updateData, headers);
      return response;
    } catch (error) {
      console.error('Update order error:', error);
      throw error;
    }
  }

  /**
   * Update order status only
   */
  async updateOrderStatus(orderId: string, statusData: UpdateOrderStatusRequest): Promise<Order> {
    try {
      const headers = getAuthHeaders();
      const response = await apiClient.patch<Order>(`/orders/${orderId}/status`, statusData, headers);
      return response;
    } catch (error) {
      console.error('Update order status error:', error);
      throw error;
    }
  }

  /**
   * Get orders by status
   */
  async getOrdersByStatus(status: Order['status'], params: { limit?: number; offset?: number } = {}): Promise<Order[]> {
    return this.getOrders({
      ...params,
      status_filter: status,
    });
  }

  /**
   * Reorder a past order — adds its items to the user's current cart.
   *
   * `mode='merge'` (default) keeps any items already in the cart and
   * sums quantities for products that match. `mode='replace'` clears
   * the existing cart first.
   */
  async reorder(orderId: string, mode: ReorderMode = 'merge'): Promise<ReorderResponse> {
    try {
      const headers = getAuthHeaders();
      return await apiClient.post<ReorderResponse>(
        `/orders/${orderId}/reorder`,
        { mode },
        headers,
      );
    } catch (error) {
      console.error('Reorder error:', error);
      throw error;
    }
  }

  /**
   * Submit order for processing
   */
  async submitOrder(orderId: string): Promise<Order> {
    try {
      const headers = getAuthHeaders();
      const response = await apiClient.put<Order>(`/orders/${orderId}/submit`, {}, headers);
      return response;
    } catch (error) {
      console.error('Submit order error:', error);
      throw error;
    }
  }
}

export const orderService = new OrderService();